import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { equipmentKeys } from '@/lib/queryKeys';
import { apiDelete, apiGet, apiPost, apiPut, operationsClient } from '@/lib/api';
import type {
  CreateEquipmentDescriptionInput,
  EquipmentDescription,
  EquipmentDescriptionFilters,
  EquipmentDescriptionListResponse,
  UpdateEquipmentDescriptionInput,
} from '@/types/equipment-description';

function buildParams(filters: EquipmentDescriptionFilters = {}): Record<string, string> {
  const params: Record<string, string> = {};
  const set = (key: string, value: string | number | undefined) => {
    if (value === undefined || value === '') return;
    params[key] = String(value);
  };
  set('status', filters.status);
  set('category', filters.category);
  set('search', filters.search);
  if (filters.unlinkedOnly) params.unlinkedOnly = 'true';
  set('limit', filters.limit);
  set('offset', filters.offset);
  return params;
}

export function useEquipmentDescriptions(
  filters: EquipmentDescriptionFilters = {},
  enabled = true,
) {
  return useApiQuery<EquipmentDescriptionListResponse>({
    queryKey: equipmentKeys.descriptions(filters),
    queryFn: ({ signal }) =>
      apiGet<EquipmentDescriptionListResponse>(operationsClient, '/equipment-descriptions', {
        params: buildParams(filters),
        signal,
      }),
    enabled,
  });
}

export function useEquipmentDescription(id: string | undefined, enabled = true) {
  return useApiQuery<EquipmentDescription>({
    queryKey: [...equipmentKeys.all(), 'description', id ?? 'none'] as const,
    queryFn: ({ signal }) =>
      apiGet<EquipmentDescription>(operationsClient, `/equipment-descriptions/${id}`, { signal }),
    enabled: enabled && !!id,
  });
}

export function useCreateEquipmentDescription() {
  const qc = useQueryClient();
  return useApiMutation<EquipmentDescription, CreateEquipmentDescriptionInput>({
    mutationFn: (input) =>
      apiPost<EquipmentDescription, CreateEquipmentDescriptionInput>(
        operationsClient,
        '/equipment-descriptions',
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...equipmentKeys.all()] });
      toast.success('Equipment description created');
    },
  });
}

export function useUpdateEquipmentDescription() {
  const qc = useQueryClient();
  return useApiMutation<
    EquipmentDescription,
    { id: string; input: UpdateEquipmentDescriptionInput }
  >({
    mutationFn: ({ id, input }) =>
      apiPut<EquipmentDescription, UpdateEquipmentDescriptionInput>(
        operationsClient,
        `/equipment-descriptions/${id}`,
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...equipmentKeys.all()] });
      toast.success('Equipment description updated');
    },
  });
}

export function useArchiveEquipmentDescription() {
  const qc = useQueryClient();
  return useApiMutation<{ archived: boolean }, string>({
    mutationFn: (id) =>
      apiDelete<{ archived: boolean }>(operationsClient, `/equipment-descriptions/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...equipmentKeys.all()] });
      toast.success('Equipment description archived');
    },
  });
}
